import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { IdealCurvePlot } from './IdealCurvePlot';
import { IdealCurvePointEditor } from './IdealCurvePointEditor';
import { IdealCurvePoint } from '../models/IdealCurvePoint'
import { Curve } from '../models/Curve';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { PlusIcon } from '@primer/octicons-react'
import { validateCurve } from '../models/Curve';
import { Polynomial } from '../models/Polynomial';
import { Exponential } from '../models/Exponential';

function curveFromData(data) {
  if (!data) return null;

  validateCurve(data);

  switch (data.type) {
    case 'Polynomial':
      return Polynomial.fromJs(data)
    case 'Exponential':
      return Exponential.fromJs(data)
    default:
      throw new Error(`Unsupported curve type: ${data.type}`)
  }
}

class IdealCurveEditor extends React.Component {
  static propTypes = {
    onCurveChange: PropTypes.func.isRequired,
    onDependencyElementChange: PropTypes.func.isRequired,
    onDependencyElementExtractionMethodChange: PropTypes.func.isRequired,
    onDirtyChange: PropTypes.func.isRequired,
    elements: PropTypes.arrayOf(PropTypes.object).isRequired,
    extraction_methods: PropTypes.arrayOf(PropTypes.object).isRequired,
    points: PropTypes.arrayOf(PropTypes.instanceOf(IdealCurvePoint)).isRequired,
    curve: PropTypes.instanceOf(Curve),
    initialDependencyElementId: PropTypes.number,
    initialDependencyElementExtractionMethodId: PropTypes.number,
    curveFitUrl: PropTypes.string.isRequired,
  };

  static defaultProps = {
    x: '',
    y: '',
    PointEditorComponent: IdealCurvePointEditor, // This is just for dependency injection in tests
  };


  constructor(props) {
    super(props);

    const { points, curve, elements, extraction_methods, initialDependencyElementId, initialDependencyElementExtractionMethodId } = this.props;

    this._csrf = document.querySelector("[name='csrf-token']")?.content;

    this.dependencyElementOptions = elements.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    }).map(element => (
      {
        value: element.id,
        label: element.name,
      }
    ));

    this.dependencyElementExtractionMethodOptions = extraction_methods.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    }).map(method => (
      {
        value: method.id,
        label: method.name,
      }
    ));

    let defaultDependencyElementOption = undefined;
    if (initialDependencyElementId) {
      defaultDependencyElementOption = this.dependencyElementOptions.find(o => o.value == initialDependencyElementId);
    }
    if (!defaultDependencyElementOption && this.dependencyElementOptions.length > 0) {
      defaultDependencyElementOption = this.dependencyElementOptions[0];
    }

    if (defaultDependencyElementOption) {
      const { onDependencyElementChange } = this.props;
      onDependencyElementChange(defaultDependencyElementOption.value);
    }

    let defaultDependencyElementExtractionMethodOption = undefined;
    if (initialDependencyElementExtractionMethodId) {
      defaultDependencyElementExtractionMethodOption = this.dependencyElementExtractionMethodOptions.find(o => o.value == initialDependencyElementExtractionMethodId);
    }
    if (!defaultDependencyElementExtractionMethodOption && this.dependencyElementExtractionMethodOptions.length > 0) {
      defaultDependencyElementExtractionMethodOption = this.dependencyElementExtractionMethodOptions[0];
    }

    if (defaultDependencyElementExtractionMethodOption) {
      const { onDependencyElementExtractionMethodChange } = this.props;
      onDependencyElementExtractionMethodChange(defaultDependencyElementExtractionMethodOption.value);
    }

    let initialUiPoints = [new IdealCurvePoint(), new IdealCurvePoint()];
    if (points.length > 0) {
      initialUiPoints = points.map(p => IdealCurvePoint.copy(p));
    }

    const initialDirty = (curve == null) && (points.length > 0);
    if (initialDirty) {
      const { onDirtyChange } = this.props;
      onDirtyChange(initialDirty);
    }

    this.state = {
      uiPoints: initialUiPoints,
      selectedDependencyElementOption: defaultDependencyElementOption,
      selectedDependencyElementExtractionMethodOption: defaultDependencyElementExtractionMethodOption,
      refreshingCurve: false,
      dirty: initialDirty,
      errorMessage: null,
    };
  }

  _handleDependencyElementOptionChange = (option) => {
    const { onDependencyElementChange } = this.props;

    this.setState({
      selectedDependencyElementOption: option,
    })

    onDependencyElementChange(option.value);
  };

  _handleDependencyElementExtractionMethodOptionChange = (option) => {
    const { onDependencyElementExtractionMethodChange } = this.props;

    this.setState({
      selectedDependencyElementExtractionMethodOption: option,
    })

    onDependencyElementExtractionMethodChange(option.value);
  };

  _handleAddNewPoint = (event) => {
    event.preventDefault();
    this.setState({
      uiPoints: [...this.state.uiPoints, new IdealCurvePoint()]
    })
  };

  _handleRemovePoint = (point) => {
    this.setState({
      uiPoints: this.state.uiPoints.filter(p => p.id != point.id),
    });
    this._setDirty(true);
  };

  _handleUpdatePoint = (point) => {
    const uiPoints = this.state.uiPoints;

    const pointIndex = uiPoints.findIndex(p => p.id == point.id);
    if (pointIndex < 0) return;

    // Replace the old point with the new one
    uiPoints.splice(pointIndex, 1, point);

    this.setState({
      uiPoints: uiPoints,
    });
    this._setDirty(true);
  };

  _changeCurve = (curve, points) => {
    const { onCurveChange } = this.props;
    const sortedPoints = points.sort((a, b) => a.x - b.x);
    this.setState({
      uiPoints: sortedPoints,
    });
    onCurveChange(curve, sortedPoints.map(p => IdealCurvePoint.copy(p)));
  };

  _setDirty = (dirty) => {
    const { onDirtyChange } = this.props;

    this.setState({
      dirty: dirty
    });
    onDirtyChange(dirty);
  };

  _refreshCurve = async () => {
    const { curveFitUrl } = this.props;
    if (!curveFitUrl) {
      throw new Error('IdealCurveEditor requires a curveFitUrl prop');
    }

    const uiPoints = this.state.uiPoints.map(p => IdealCurvePoint.copy(p));

    // Before fetching the curve, validate the points. We do it with a map
    // instead of using `every` directly so that we don't short-circuit the
    // validity check, which will result in validation errors being shown
    // where required instead of just on the first one.
    const validPoints = uiPoints?.map(p => p.valid());

    this.setState({
      uiPoints: uiPoints, // .valid() updated these
    })

    if (uiPoints == null || uiPoints.length == 0 || !validPoints.every(Boolean)) {
      return;
    }

    this.setState({
      refreshingCurve: true,
    })

    try {
      const response = await fetch(curveFitUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this._csrf,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ points: uiPoints.map(p => p.toJs()) }),
      });

      if (response.ok) {
        const data = await response.json();

        this._changeCurve(curveFromData(data), uiPoints)
        this._setDirty(false);
        this.setState({
          errorMessage: null,
        });
      } else {
        let data;
        let message;

        try {
          data = await response.json();
          message = data.errors.join(', ');
        } catch {
          message = `got HTTP ${response.status}`;
        }

        throw new Error(message);
      }
    }
    catch (error) {
      this.setState({
        errorMessage: `Unable to refresh curve: ${error.message}`,
      })
    }
    finally {
      this.setState({
        refreshingCurve: false,
      });
    }
  };

  _renderError = () => {
    if (this.state.errorMessage == null) return null;

    return (
      <div className="error p-2 mb-2">{this.state.errorMessage}</div>
    );
  };

  render() {
    const { yLabel, points, curve, PointEditorComponent } = this.props;

    return (
      <Container fluid>
        <h2 className="mb-2">Select grid value to use in calculation</h2>
        <Select aria-label="Dependency Element Selector" options={this.dependencyElementOptions} isClearable={false} value={this.state.selectedDependencyElementOption} onChange={this._handleDependencyElementOptionChange} />

        <h2 className="mb-2">Select extraction method of above grid value</h2>
        <Select aria-label="Dependency Element Extraction Method Selector" options={this.dependencyElementExtractionMethodOptions} isClearable={false} value={this.state.selectedDependencyElementExtractionMethodOption} onChange={this._handleDependencyElementExtractionMethodOptionChange} />

        <div style={{ padding: '0.2em', marginTop: '2em' }}>
          <IdealCurvePlot curve={curve} points={points} yLabel={yLabel} xLabel={this.state.selectedDependencyElementOption?.label} />
        </div>

        <h2 className="mb-2">Add points to curve</h2>

        <p>Add measured points to the list and the system will try to find a curve that represents that relationship.</p>

        <Row>
          <Col className="text-right"><a href="#" onClick={this._handleAddNewPoint}><PlusIcon /> Add point</a></Col>
        </Row>
        <Row>
          <Col><h2 className="text-center mb-2">{this.state.selectedDependencyElementOption?.label}</h2></Col>
          <Col><h2 className="text-center mb-2">{yLabel}</h2></Col>
          <Col xs={1}></Col>
        </Row>
        {this.state.uiPoints.map(point => (
          <PointEditorComponent key={point.id} point={point} onChange={this._handleUpdatePoint} onRemove={this._handleRemovePoint} />
        ))}

        {this._renderError()}

        <Button variant="primary" onClick={this._refreshCurve} disabled={this.state.refreshingCurve || !this.state.dirty}>{this.state.refreshingCurve ? 'Refreshing...' : 'Refresh Curve'}</Button>
      </Container>
    );
  }
}

export { IdealCurveEditor, curveFromData };
